import { ServerErrorResponseException, ServiceException } from '@inter-ikea-kompis/exceptions';
import ServiceFetch from '../../utilities/ServiceFetch.js';
/**
 * OneWeb financing service.
 */
export default class OneWebFinancingService {
    /**
     * @param endpoint The endpoint to the API.
     * @param fragmentLocationBaseUrl The base URL of the fragment content location, needed for proxy server.
     * @param price The price of the solution.
     * @returns Promise of financing option.
     * @throws ServiceException if price is negative or if any part of the fetched financing option data is invalid.
     * @throws ServerErrorResponseException if API endpoint is not found or if fetch to API endpoint fails.
     */
    async getFinancingOption(endpoint, fragmentLocationBaseUrl, price) {
        if (price < 0) {
            throw new ServiceException('Negative price.');
        }
        const priceWithTwoDecimals = price.toFixed(2);
        const endpointWithPrice = `${endpoint.replace('{totalPrice}', priceWithTwoDecimals)}`;
        const response = await fetch(endpointWithPrice);
        this.validateResponse(response);
        const data = await response.json();
        const { anchor, anchorLink, fragmentLocation, moreInfoAnchor, moreInfoLink } = this.getValidFinancingOptionData(data);
        const content = await this.fetchFragmentContent(fragmentLocation, fragmentLocationBaseUrl);
        const financingOption = {
            anchor,
            anchorLink,
            content,
            moreInfoAnchor,
            moreInfoLink
        };
        return financingOption;
    }
    /**
     * Validate the reponse.
     *
     * @param response The response from INGKA financing service API.
     */
    validateResponse(response) {
        if (response.status === 404) {
            throw new ServerErrorResponseException(response, 'There was no financing option available for the market.');
        }
        if (!response.ok) {
            throw new ServerErrorResponseException(response, 'Something went wrong when fetching from Ingka financing service.');
        }
    }
    /**
     * Get the validated financing option data.
     *
     * @param data Unvalidated JSON data from the external API, in camel case format.
     * @param data.anchor Anchor text used for button/link.
     * @param data.anchor_link Optional anchor link for anchor.
     * @param data.fragment_location URL to fragment.
     * @param data.read_more_anchor Anchor text for "read more" link.
     * @param data.read_more_link Link used as href when read_more_anchor is clicked.
     */
    getValidFinancingOptionData(data) {
        return {
            anchor: this.getValidatedPropWithLength(data.anchor, 'anchor'),
            anchorLink: data.anchor_link ?? null,
            fragmentLocation: this.getValidatedPropWithLength(data.fragment_location, 'fragment_location'),
            moreInfoAnchor: this.getValidatedPropWithLength(data.read_more_anchor, 'read_more_anchor'),
            moreInfoLink: this.getValidatedPropWithLength(data.read_more_link, 'read_more_link')
        };
    }
    /**
     * @param prop The prop to validate.
     * @param propName The name of the property.
     * @returns The validated prop if valid.
     * @throws ServiceException if invalid prop.
     */
    getValidatedPropWithLength(prop, propName) {
        if (prop?.length) {
            return prop;
        }
        throw new ServiceException(`Invalid financing option. Missing ${propName} or it is empty.`);
    }
    /**
     * @param fragmentLocation The fragment location.
     * @param fragmentLocationBaseUrl The base URL of the fragment content location, needed for proxy server.
     * @returns The HTML content of the fragment as text.
     */
    async fetchFragmentContent(fragmentLocation, fragmentLocationBaseUrl) {
        const updatedFragmentLocation = fragmentLocation.replace('https://www.ikea.com', fragmentLocationBaseUrl);
        const content = await ServiceFetch.text(updatedFragmentLocation);
        if (!content?.length) {
            throw new ServiceException('No fragment content found.');
        }
        return content;
    }
}
//# sourceMappingURL=OneWebFinancingService.js.map